import { NetworkEnum, NetworkSettingsConfig } from "@/config/config.types";
import { WalletInfo, WalletType } from "./types";
import { ChainNetwork, ProtocolProfile, VaultWallet, WalletAccountRecord, WalletConfig } from "@ducat-unit/client-sdk";
import { getWalletConnector } from "@/lib/util";
import { WalletConnector } from "@/lib/connector";
import { getLogger } from '@/utils/loglevel';

const logger = getLogger('context.helpers', 'debug');

export async function connectWallet(params: {
  config: NetworkSettingsConfig;
  contract: ProtocolProfile | null;
  walletType: WalletType;
}): Promise<{
  wallet: VaultWallet;
  isValid: boolean;
  errorMessage: string;
}> {
  const { contract, config, walletType } = params;
  if (!contract) {
    throw Error('Protocol contract is not ready');
  }
  const walletConnector = getWalletConnector(walletType);
  if (!walletConnector) {
    throw Error(`Cant find wallet connector for ${walletType}`);
  }
  // Fetch accounts from wallet.
  const accounts = await walletConnector.getWalletAccounts();
  console.log(`- accounts:`, accounts);
  const { isValid, errorMessage } = walletConnector.isValidWalletAccountRecord(accounts);

  return {
    wallet: configureVaultWallet({ contract, accounts, config, walletConnector }),
    isValid,
    errorMessage
  };
}

export async function reconnectWallet(params: {
  walletInfo: WalletInfo;
  config: NetworkSettingsConfig;
  contract: ProtocolProfile;
}): Promise<{
  wallet: VaultWallet;
  walletType: WalletType;
  isValid: boolean;
  errorMessage: string;
}> {
  const { walletInfo, contract, config } = params;
  logger.debug(`[reconnectWallet]`, params);
  const walletConnector = getWalletConnector(walletInfo.type);
  if (!walletConnector) {
    throw Error(`Cant find wallet connector for ${walletInfo.type}`);
  }

  const accounts = await walletConnector.reconnect?.(walletInfo);
  if (!accounts) {
    throw Error('Was not able to retrieve wallet accounts');
  }
  logger.debug(`Wallet reconnected. Accounts:`, accounts);
  const wallet = configureVaultWallet({ contract, accounts, config, walletConnector });
  const { isValid, errorMessage } = walletConnector.isValidWalletAccountRecord(accounts);

  return {
    wallet,
    walletType: walletInfo.type,
    isValid,
    errorMessage
  };
}

export function configureVaultWallet(params: {
  accounts: WalletAccountRecord;
  config: NetworkSettingsConfig;
  contract: ProtocolProfile;
  walletConnector: WalletConnector;
}): VaultWallet {
  const { accounts, config, contract, walletConnector } = params;
  const WALLET_CFG: WalletConfig = {
    indexer: {
      esp: config.esploraUrl,
      ord: config.ordUrl,
    },
    network: (config.network === NetworkEnum.MUTINYNET ? 'mutiny' : config.network) as ChainNetwork,
    postage: {
      unit: 10_000,
      vault: 10_000,
    },
  };
  const wallet = new VaultWallet(
    accounts,
    contract,
    walletConnector.api,
    WALLET_CFG
  );

  return wallet;
}
